/** @jsx */
import { css } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import * as Sentry from '@sentry/react';
import type { LearnerProfile_Specialization as LearnerProfileSpecialization } from '__generated__/graphql-types-do-not-use';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';

import { Grid, Link, Typography2 } from '@coursera/cds-core';
import { DegreeIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import {
  getCredentialName,
  getViewCertificateUrl,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/credentialUtils';
import type { CredentialTypeValues } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/credentialUtils';
import { isSSR } from 'bundles/ondemand/utils/isSSR';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type ItemProps = {
  credentialType: CredentialTypeValues;
  credential?: LearnerProfileSpecialization;
};

type ItemPropsWithDefinedCredential = ItemProps & {
  credential: LearnerProfileSpecialization;
};

const styles = {
  credentialWrapper: () => css`
    display: flex;
    gap: var(--cds-spacing-150);
  `,
};

const HighlightsSectionCredentialsItem = ({ credential, credentialType }: ItemPropsWithDefinedCredential) => {
  const { retrackedEventingData, sharedEventingV3Data } = useContext(ProfileContext);
  const certificateUrl = getViewCertificateUrl(credential);
  const retrackedTrack = useRetracked();
  const track = useTracker();
  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.openHighlightsCertificateItem,
      linkType: 'internal',
      linkURL: certificateUrl,
    },
    product: {
      id: credential.productId,
    },
    ...sharedEventingV3Data,
  });

  /* TODO: Investigate missing partner info in some cases: https://coursera.atlassian.net/browse/LPD-477 */
  if (!credential.partners?.length && !isSSR()) {
    Sentry.captureException(new Error('Missing partner info in credential'));
  }

  return (
    <Grid item css={styles.credentialWrapper} key={credential.verifyCode} data-testid="credential-item">
      {/*  TODO: Investigate missing partner info in some cases: https://coursera.atlassian.net/browse/LPD-477 */}
      {credential.partners?.length ? (
        <Imgix maxHeight={36} maxWidth={36} src={credential.partners[0].logoUrl} alt={`${credential.name}_logo`} />
      ) : (
        <DegreeIcon height={72} width={72} />
      )}
      <div>
        <Typography2 component="p" variant="bodySecondary">
          {getCredentialName(credential, credentialType)}
        </Typography2>
        {/* I wrap the button with a TrackedDiv to support retracked visibility tracking since I cannot use tracked components on
        CDS buttons. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
        {TrackedLink2} on a CDS link causes the ref to refer to the TrackedLink component itself, *not* the
        underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
        error out since we're not referencing an html element. */}
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          requireFullyVisible={false}
          trackingName={EVENTS.openHighlightsCertificateItem}
          data={{ ...retrackedEventingData, product: { productId: credential.productId } }}
        >
          <Link
            key={`link~${credential.verifyCode}`}
            ref={visibilityTrackingRef}
            href={certificateUrl}
            onClick={() => {
              retrackedTrack({
                trackingData: { ...retrackedEventingData, product: { productId: credential.productId } },
                trackingName: EVENTS.openHighlightsCertificateItem,
                action: 'click',
              });

              track('click_button', {
                button: {
                  name: EVENTS.openHighlightsCertificateItem,
                  linkType: 'internal',
                  linkURL: certificateUrl,
                },
                product: {
                  id: credential.productId,
                },
                ...sharedEventingV3Data,
              });
            }}
            variant="standard"
            typographyVariant="bodySecondary"
            target="_blank"
          >
            {_t('View certificate')}
          </Link>
        </TrackedDiv>
      </div>
    </Grid>
  );
};

// I need to make a wrapper to check if the credential is defined, so that we can pass in the product id to the tracking data since it's required
// and can't be undefined
const HighlightsSectionCredentialsItemWrapper = (props: ItemProps) => {
  if (!props.credential) {
    return null;
  }

  return <HighlightsSectionCredentialsItem {...(props as ItemPropsWithDefinedCredential)} />;
};

export default HighlightsSectionCredentialsItemWrapper;
